import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';

interface Props {
  open: boolean;
  title: string;
  children?: JSX.Element | JSX.Element[];
  className?: string;
  onClose?: () => void;
}

/**
 * Modal component
 */
export const Modal = ({ open, onClose, title, children, className }: Props) => {
  // disable scroll while modal is open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    if (!open) document.body.style.overflow = 'auto';
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      hidden={!open}
      className="relative z-10"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

      <div className="fixed inset-0 z-10 overflow-y-auto overflow-auto">
        <div className="flex min-h-full items-end justify-center p-2 text-center sm:items-center sm:p-0">
          <div
            className={`relative p-5 transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg ${className}`}
          >
            <div className="flex flex-row justify-between">
              <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                {title}
              </h3>
              {onClose && (
                <XMarkIcon
                  className="h-6 w-6 ml-1 cursor-pointer hover:text-blue-500"
                  onClick={onClose}
                />
              )}
            </div>
            <hr className="mt-2" />
            <div className="bg-white px-4 sm:pt-1 sm:pb-1">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
